import React, { useContext, useEffect, useRef, useState } from 'react'
import { createMarkupWithOption } from '../../models/Common'
import {
  IQuestionFailedResource,
  IQuizResource,
  IWorkplaceQuizState,
} from '../../models/WorkplaceQuiz'
import Conditional from '../common/Conditional'
import WorkplaceQuizContext from './context/WorkplaceQuizContext'
import WorkplaceResultsResourceItemLink from './WorkplaceResultsResourceItemLink'

export interface IWorkplaceQuizModuleFinishedProps {
  quizId: number
  summaryContent: string
  summaryAfterContent: string
  dynamicContent: string
  fixedContent: string
  resources: IQuizResource[]
  completed: () => void
  startAgain: () => void
}

const WorkplaceQuizModuleFinished = (
  props: IWorkplaceQuizModuleFinishedProps
) => {
  const mountedRef = useRef(false)
  const context = useContext(WorkplaceQuizContext)
  const [isLoading, setIsLoading] = useState(true)
  const [quizState, setQuizState] = useState<IWorkplaceQuizState>()
  const [content, setContent] = useState({
    quizId: null,
    summaryContent: '',
    summaryAfterContent: '',
    dynamicContent: '',
    fixedContent: '',
    resources: [],
  })
  const govCMSHome = process.env.REACT_APP_GOVCMS_HOME

  useEffect(() => {
    mountedRef.current = true
    if (mountedRef.current) {
      const quizState = context.getQuizState(props.quizId)
      setQuizState(quizState)
      setContent((prevState) => ({
        ...prevState,
        quizId: props.quizId,
        summaryContent: props.summaryContent,
        summaryAfterContent: props.summaryAfterContent,
        dynamicContent: props.dynamicContent,
        fixedContent: props.fixedContent,
        resources: props.resources,
      }))
      setIsLoading(false)
    }
    return () => {
      mountedRef.current = false
    }
  }, [props.quizId])

  const complete = () => {
    props.completed()
  }
  const onKeyPressCompleted = (event) => {
    if (event.keyCode === 13) {
      complete()
    }
  }
  const onKeyPressStartAgain = (event) => {
    if (event.keyCode === 13) {
      startAgain()
    }
  }

  const startAgain = () => {
    props.startAgain()
  }

  return (
    <Conditional if={!isLoading}>
      <div className="scoreArea">
        <p className="status">Completed</p>
        <p className="text">Your score for this module:</p>
        <p className="score">
          {quizState?.correctAnswered?.length}/{quizState?.totalQuestions}
        </p>
        <Conditional if={content?.summaryContent}>
          <div
            className="summaryArea"
            dangerouslySetInnerHTML={createMarkupWithOption(
              content?.summaryContent,
              { ADD_ATTR: ['target'] }
            )}
          ></div>
        </Conditional>

        <div className="linksArea">
          <div className="linkArea">
            <ul>
              {quizState?.remainderResources.map(
                (item: IQuizResource, index) => {
                  return (
                    <WorkplaceResultsResourceItemLink
                      key={index}
                      url={govCMSHome + item.link}
                      content={item.content}
                    />
                  )
                }
              )}
              {quizState?.failedResources.map(
                (item: IQuestionFailedResource, index) => {
                  return (
                    <WorkplaceResultsResourceItemLink
                      key={index}
                      url={govCMSHome + item.resource?.link}
                      content={item.resource?.content}
                    />
                  )
                }
              )}
            </ul>
          </div>
        </div>

        <Conditional if={content?.summaryAfterContent}>
          <div
            className="summaryAreaAfter"
            dangerouslySetInnerHTML={createMarkupWithOption(
              content?.summaryAfterContent,
              { ADD_ATTR: ['target'] }
            )}
          ></div>
        </Conditional>
        <div className="navButtons">
          <a
            className="button is-secondary"
            onClick={() => startAgain()}
            onKeyDown={onKeyPressStartAgain}
            role="button"
            tabIndex={0}
          >
            Start again
          </a>

          <a
            className="button is-primary"
            onClick={() => complete()}
            onKeyDown={onKeyPressCompleted}
            role="button"
            tabIndex={0}
          >
            Complete
          </a>
        </div>
      </div>
    </Conditional>
  )
}

export default WorkplaceQuizModuleFinished
